// src/components/UserMatchCard.jsx
import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CalendarDays,
  CheckCircle2,
  Clock3,
  EllipsisVertical,
  Play,
  Trash2,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import TeamLogo from "@/components/TeamLogo";
import { formatSvDateTime } from "@/utils/date";

const STATUS_LABEL = {
  validated: "Validerad",
  disputed: "Avvikelse",
  completed: "Komplett",
  complete: "Komplett",
};

const STATUS_VARIANT = {
  validated: "default",
  disputed: "destructive",
  completed: "secondary",
  complete: "secondary",
};

function TeamColumn({ teamName, fallbackLetter }) {
  return (
    <div className="flex min-w-0 flex-1 flex-col items-center gap-1.5 text-center">
      <div className="relative h-11 w-11 shrink-0 rounded-full border border-border/70 bg-muted/30 sm:h-12 sm:w-12">
        <TeamLogo
          teamName={teamName}
          size={34}
          className="absolute inset-0 m-auto h-[34px] w-[34px] object-contain sm:h-9 sm:w-9"
          fallback={
            <div className="absolute inset-0 flex items-center justify-center text-xs font-semibold uppercase text-muted-foreground">
              {fallbackLetter}
            </div>
          }
        />
      </div>
      <span className="max-w-full text-xs font-semibold text-foreground sm:text-sm">{teamName}</span>
    </div>
  );
}

function splitDateTimeLabel(dateLabel) {
  if (!dateLabel) return { datePart: "Datum saknas", timePart: "" };
  const match = String(dateLabel).match(/^(.*)\s(\d{2}:\d{2})$/);
  if (match) {
    return {
      datePart: match[1].trim(),
      timePart: match[2],
    };
  }
  return { datePart: dateLabel, timePart: "" };
}

export default function UserMatchCard({
  userMatch,
  onDelete,
  detailBasePath = "/account/matches/saved",
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [deleteOpen, setDeleteOpen] = useState(false);

  const id = userMatch?.id ?? userMatch?._id;
  const status = (userMatch?.status || "complete").toLowerCase();

  const { homeTeam, awayTeam, score, dateLabel } = useMemo(() => {
    const home = userMatch?.match_details?.home_team ?? "Hemmalag";
    const away = userMatch?.match_details?.away_team ?? "Bortalag";
    const userScore = userMatch?.user_results || {};

    return {
      homeTeam: home,
      awayTeam: away,
      score: `${userScore.home_score ?? 0} - ${userScore.away_score ?? 0}`,
      dateLabel:
        formatSvDateTime(userMatch?.match_details?.date || userMatch?.completed_at, {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }) || "Datum saknas",
    };
  }, [userMatch]);

  const officialScore = useMemo(() => {
    if (!userMatch?.official_results) return null;
    const { home_score, away_score } = userMatch.official_results;
    if (home_score == null || away_score == null) return null;
    return `${home_score} - ${away_score}`;
  }, [userMatch]);

  const statusLabel = STATUS_LABEL[status] || "Komplett";
  const statusVariant = STATUS_VARIANT[status] || "secondary";
  const { datePart, timePart } = useMemo(() => splitDateTimeLabel(dateLabel), [dateLabel]);

  const goToDetail = () => {
    if (!id) return;
    navigate(`${detailBasePath}/${id}`, {
      state: { returnTo: `${location.pathname}${location.search}` },
    });
  };

  return (
    <>
      <Card
        role="button"
        tabIndex={0}
        onClick={goToDetail}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            goToDetail();
          }
        }}
        className="protocol-list-card protocol-list-card-saved group cursor-pointer overflow-hidden border transition hover:border-primary/60 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        <CardContent className="p-2.5 sm:p-3">
          <div className="relative flex items-center justify-between gap-2">
            <div>
              <Badge variant={statusVariant}>{statusLabel}</Badge>
            </div>
            <div className="pointer-events-none absolute inset-x-0 flex justify-center">
              {timePart ? (
                <div className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground sm:text-sm">
                  <Clock3 className="h-4 w-4" />
                  <span>{timePart}</span>
                </div>
              ) : null}
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="native-v3-protocol-menu-trigger h-8 w-8 rounded-full"
                  onClick={(event) => event.stopPropagation()}
                  onPointerDown={(event) => event.stopPropagation()}
                >
                  <EllipsisVertical className="h-4 w-4" />
                  <span className="sr-only">Fler val</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="native-v3-protocol-menu w-48 border-border bg-background shadow-md"
                onClick={(event) => event.stopPropagation()}
                onPointerDown={(event) => event.stopPropagation()}
              >
                <DropdownMenuItem
                  className="native-v3-protocol-menu-item"
                  onClick={(event) => {
                    event.stopPropagation();
                    goToDetail();
                  }}
                >
                  <Play className="h-4 w-4" />
                  Visa protokoll
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="native-v3-protocol-menu-item native-v3-protocol-menu-item-danger text-destructive focus:text-destructive"
                  onClick={(event) => {
                    event.stopPropagation();
                    setDeleteOpen(true);
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                  Ta bort
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="mt-1 flex items-start gap-2">
            <TeamColumn teamName={homeTeam} fallbackLetter="H" />

            <div className="flex shrink-0 flex-col items-center justify-center gap-1.5 px-1 text-center">
              <div className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
                {score}
              </div>
            </div>

            <TeamColumn teamName={awayTeam} fallbackLetter="B" />
          </div>

          <div className="mt-3 border-t border-border/70 pt-2.5 text-xs text-muted-foreground sm:text-sm">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <CalendarDays className="h-4 w-4" />
              <span>{datePart}</span>
              {officialScore ? (
                <>
                  <span>•</span>
                  <span className="inline-flex items-center gap-1.5">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    <span>
                      Officiellt resultat <span className="font-medium text-foreground">{officialScore}</span>
                    </span>
                  </span>
                </>
              ) : null}
            </div>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ta bort sparad match?</AlertDialogTitle>
            <AlertDialogDescription>
              Tar bort posten från Mina matcher och det underliggande protokollet.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => onDelete?.(id, userMatch?.match_id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Ta bort
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
